import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { AuthorizationModule } from '@wilson/authorization';
import {
  Activity,
  ACTIVITY_BODY_HEIGHT_PX,
  ActivityDeviationStatus,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import {
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  TimeLineActivityTooltipComponent,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { DragAndDropModeService } from '@wilson/shift-timeline/services';
import { differenceInMilliseconds } from 'date-fns';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { ActivityCardContentComponent } from '../../activity-card-content/activity-card-content.component';
import { ActivityDragHandlerDirective } from './activity-drag-handler.directive';
/**
 * Deprecated
 */
@Component({
  selector: 'wilson-timeline-auto-size-activity-card',
  templateUrl: './timeline-auto-size-activity-card.component.html',
  styleUrls: ['./timeline-auto-size-activity-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzTooltipModule,
    TimeLineActivityTooltipComponent,
    ActivityCardContentComponent,
    ActivityDragHandlerDirective,
    GanttEndingPixelPipe,
    GanttStartingPixelPipe,
    AuthorizationModule,
  ],
})
export class TimelineAutoSizeActivityCardComponent
  implements OnInit, AfterViewInit
{
  private readonly cdRef = inject(ChangeDetectorRef);
  readonly dragAndDropModeService = inject(DragAndDropModeService);

  ACTIVITY_BODY_HEIGHT_PX = ACTIVITY_BODY_HEIGHT_PX;
  containerWidthPxSignal = signal(0);
  private expectedLeftPxSignal = signal(0);

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  can = inject(AbilityServiceSignal).can;
  @Input() activity!: ActivityWithCategoryAndLocationsAndReportedDetails;
  @Input() userId!: string | null;
  @Input() accurateActivityStartDateTime!: string;
  @Input() activityStartTimeDifference!: number;
  @Input() accurateActivityEndDateTime!: string;
  @Input() activityEndTimeDifference!: number;
  @Input() activityStartTimeType!: ActivityDeviationStatus;
  @Input() activityEndTimeType!: ActivityDeviationStatus;
  @Input() accurateContainerStartDateTime!: string;
  @Input() accurateContainerEndDateTime!: string;
  @Input() shiftPublicationStatus!: PublicationStatus | null;
  @Input() shiftActivities!: Activity[] | null;
  @Input() set containerWidthPx(value: number | null) {
    if (typeof value === 'number') {
      this.containerWidthPxSignal.set(value);
    }
  }
  @Input() accurateContainerDurationMs!: number;
  @Input() filteredActivityId!: string | null;
  @Input() isActivityStartLocationMismatch!: boolean;
  @Input() isActivityEndLocationMismatch!: boolean;
  @Input() isCursorGrab!: boolean;
  @Input() isDraggable!: boolean;

  activityWidth = computed(() => {
    let width = 0;
    const shiftWidthPx = this.containerWidthPxSignal();
    if (
      shiftWidthPx &&
      this.accurateActivityEndDateTime &&
      this.accurateActivityStartDateTime &&
      this.accurateContainerDurationMs
    ) {
      const activityDurationMs = differenceInMilliseconds(
        new Date(this.accurateActivityEndDateTime),
        new Date(this.accurateActivityStartDateTime),
      );

      width =
        shiftWidthPx *
        (activityDurationMs / Math.abs(this.accurateContainerDurationMs));
    }

    return width;
  });

  adjustedLeftPositionSignal = computed(() => {
    return this.expectedLeftPxSignal();
  });

  isTooltipVisible = signal(true);
  isFiltered = true;
  isStretchingFromLeft = false;
  isStretchingFromRight = false;

  ngOnInit(): void {
    this.expectedLeftPxSignal.set(this.calculateExpectedLeftPx());
  }

  private calculateExpectedLeftPx(): number {
    const startTimeDifferenceInMs = differenceInMilliseconds(
      new Date(this.accurateActivityStartDateTime),
      new Date(this.accurateContainerStartDateTime),
    );

    return (
      (this.containerWidthPxSignal() * startTimeDifferenceInMs) /
      this.accurateContainerDurationMs
    );
  }

  stopEventFromBubbling(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  ngAfterViewInit(): void {
    this.cdRef.detectChanges();
  }

  setPreviewForMoveMode(
    event: DragEvent,
    isMoveMode: boolean | null,
    previewImage: HTMLElement,
    hasServicetUpdatePermissions: boolean,
  ): void {
    if (isMoveMode && hasServicetUpdatePermissions) {
      event.dataTransfer?.setDragImage(previewImage, 0, 0);
    }
  }
}
